/**
 * The template's dropdown and date picker.
 *
 * Ynex never shows the browser's own select list or calendar: its forms use react-select
 * with the "Select2" class prefix, and react-datepicker inside an input group with a
 * calendar icon, both styled by the template's stylesheet. These wrap the two so a page
 * passes a value and gets a value back, the same as with a plain input.
 */

import DatePicker from "react-datepicker";
import Select, { type SingleValue } from "react-select";
import CreatableSelect from "react-select/creatable";

export interface Choice<T extends string> {
  readonly value: T;
  readonly label: string;
}

/*
 * The open list is drawn on the page itself rather than inside its box, so a table that
 * scrolls sideways or a modal cannot cut it off. It sits above the modal (60) and the
 * offcanvas (150).
 */
const ABOVE_OVERLAYS = 200;

export function SelectField<T extends string>({
  id,
  value,
  options,
  onChange,
  placeholder = "Select",
  disabled = false,
  label,
  className = "",
}: {
  readonly id: string;
  readonly value: T | "";
  readonly options: readonly Choice<T>[];
  readonly onChange: (value: T) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** For a dropdown with no visible label, such as one inside a table row. */
  readonly label?: string;
  readonly className?: string;
}): React.ReactElement {
  return (
    <Select<Choice<T>, false>
      inputId={id}
      className={`basic-multi-select ${className}`}
      classNamePrefix="Select2"
      menuPlacement="auto"
      menuPortalTarget={typeof document === "undefined" ? null : document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: ABOVE_OVERLAYS }) }}
      // Marks a list that opened upwards for lack of room, so theme/additions.css leaves it there.
      classNames={{ menu: (state) => (state.placement === "top" ? "Select2__menu--up" : "") }}
      options={options}
      value={options.find((option) => option.value === value) ?? null}
      placeholder={placeholder}
      isDisabled={disabled}
      // Typing to find only helps in a long list; in a short one it just opens a keyboard.
      isSearchable={options.length > 8}
      {...(label ? { "aria-label": label } : {})}
      onChange={(choice: SingleValue<Choice<T>>) => {
        if (choice) onChange(choice.value);
      }}
    />
  );
}

/**
 * A list to pick from that also takes something new: a game, a category, a section. Every
 * choice shows when it opens, whatever is already in it. The browser's own suggestions
 * showed only what matched the box, so with Pokémon in it Riftbound and Lorcana could not
 * be picked (D, 4 October 2026).
 */
export function ChoiceField({
  id,
  value,
  options,
  onChange,
  placeholder = "Select or type",
}: {
  readonly id: string;
  readonly value: string;
  readonly options: readonly string[];
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
}): React.ReactElement {
  const choices = options.map((one) => ({ value: one, label: one }));
  const current = value.trim() ? (choices.find((choice) => choice.value === value) ?? { value, label: value }) : null;
  return (
    <CreatableSelect<Choice<string>, false>
      inputId={id}
      className="basic-multi-select"
      classNamePrefix="Select2"
      menuPlacement="auto"
      menuPortalTarget={typeof document === "undefined" ? null : document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: ABOVE_OVERLAYS }) }}
      classNames={{ menu: (state) => (state.placement === "top" ? "Select2__menu--up" : "") }}
      options={choices}
      value={current}
      placeholder={placeholder}
      isClearable
      formatCreateLabel={(typed) => `Add "${typed}"`}
      onChange={(choice: SingleValue<Choice<string>>) => onChange(choice?.value ?? "")}
    />
  );
}

/** A "YYYY-MM-DD" day as a local date, without the time zone shift Date parsing adds. */
function fromDay(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function toDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The template's date field: a calendar icon, then the picker. Dates read "22 Sep 2026". */
export function DateField({
  id,
  value,
  onChange,
  disabled = false,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (day: string) => void;
  readonly disabled?: boolean;
}): React.ReactElement {
  return (
    <div className="input-group !flex-nowrap">
      <div className="input-group-text text-[#8c9097] dark:text-white/50">
        <i className="ri-calendar-line" />
      </div>
      <DatePicker
        id={id}
        className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
        wrapperClassName="flex-1"
        // A form opening with the date first should not open a calendar over the form.
        preventOpenOnFocus
        selected={fromDay(value)}
        dateFormat="d MMM yyyy"
        disabled={disabled}
        onChange={(date: Date | null) => {
          if (date) onChange(toDay(date));
        }}
      />
    </div>
  );
}
